/**
 * Invoices Module
 *
 * Invoice status checking after payment.
 */

import { get } from '../core/client';
import { buildEndpoint } from '../core/endpoint';
import type { SDKResponse, Invoice } from '../types';

function normalizeInvoiceId(invoiceId: string): string | null {
  const normalized = invoiceId.trim();
  if (!normalized) {
    return null;
  }
  return normalized;
}

export async function getInvoice(
  invoiceId: string
): Promise<SDKResponse<Invoice>> {
  const normalizedInvoiceId = normalizeInvoiceId(invoiceId);
  if (!normalizedInvoiceId) {
    return {
      success: false,
      message: 'Invoice ID is required',
    };
  }

  const response = await get<{ invoice: Invoice }>(
    buildEndpoint('/v1/storefront/invoices/unique/:invoiceId', {
      invoiceId: normalizedInvoiceId,
    })
  );
  if (!response.success) {
    return {
      success: false,
      message: response.message,
    };
  }

  if (!response.data?.invoice) {
    return {
      success: false,
      message: 'Invalid invoice response',
    };
  }

  return {
    success: true,
    data: response.data.invoice,
  };
}

export async function getInvoiceStatus(
  invoiceId: string
): Promise<SDKResponse<{ status: string }>> {
  const normalizedInvoiceId = normalizeInvoiceId(invoiceId);
  if (!normalizedInvoiceId) {
    return {
      success: false,
      message: 'Invoice ID is required',
    };
  }

  const response = await get<{ invoice: { status: string } }>(
    buildEndpoint('/v1/storefront/invoices/status/:invoiceId', {
      invoiceId: normalizedInvoiceId,
    })
  );
  if (!response.success) {
    return {
      success: false,
      message: response.message,
    };
  }

  if (!response.data?.invoice?.status) {
    return {
      success: false,
      message: 'Invalid invoice status response',
    };
  }

  return {
    success: true,
    data: { status: response.data.invoice.status },
  };
}
